/**
 * SIMIL-R - Wishlist (Liste de souhaits)
 * Gestion du stockage local, synchronisation du compteur d'en-tête,
 * bascule favoris sur miniatures/fiches, grille dynamique et ajout panier rapide.
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'similr_wishlist_items';

  class SimilrWishlist {
    constructor() {
      this.items = this.loadItems();
      this.init();
    }

    loadItems() {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed)) return parsed;
        }
      } catch (e) {
        console.warn('[Similr Wishlist] Erreur lecture localStorage', e);
      }
      return [];
    }

    saveItems() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.items));
      } catch (e) {
        console.warn('[Similr Wishlist] Erreur écriture localStorage', e);
      }
    }

    getItems() {
      return this.items;
    }

    hasItem(id) {
      if (!id) return false;
      const strId = String(id);
      return this.items.some((item) => String(item.id) === strId);
    }

    addItem(item) {
      if (!item || !item.id) return;
      if (!this.hasItem(item.id)) {
        this.items.push(item);
        this.saveItems();
        this.updateUI();
        this.showToast('Ajouté aux favoris', 'added');
        document.dispatchEvent(new CustomEvent('wishlist:itemAdded', { detail: { item } }));
      }
    }

    removeItem(id) {
      const strId = String(id);
      const initialCount = this.items.length;
      this.items = this.items.filter((item) => String(item.id) !== strId);
      if (this.items.length !== initialCount) {
        this.saveItems();
        this.updateUI();
        this.showToast('Retiré des favoris', 'removed');
        document.dispatchEvent(new CustomEvent('wishlist:itemRemoved', { detail: { id } }));
      }
    }

    toggleItem(item) {
      if (this.hasItem(item.id)) {
        this.removeItem(item.id);
        return false;
      } else {
        this.addItem(item);
        return true;
      }
    }

    updateUI() {
      this.updateHeaderBadge();
      this.updateHeartButtons();
      this.updateWishlistPage();
    }

    updateHeaderBadge() {
      const count = this.items.length;
      const headerIcons = document.querySelectorAll('.header__icon--wishlist, #wishlist-icon-bubble');

      headerIcons.forEach((icon) => {
        if (count > 0) {
          icon.classList.add('has-items');
        } else {
          icon.classList.remove('has-items');
        }

        const countBubbles = icon.querySelectorAll('.wishlist-count-bubble, [data-wishlist-count]');
        countBubbles.forEach((bubble) => {
          const numSpan = bubble.querySelector('span[aria-hidden="true"]') || bubble;
          numSpan.textContent = count;
          bubble.style.display = count > 0 ? 'flex' : 'none';
        });
      });
    }

    updateHeartButtons() {
      const buttons = document.querySelectorAll('[data-wishlist-btn]');
      buttons.forEach((btn) => {
        const id = btn.getAttribute('data-product-id');
        const isActive = this.hasItem(id);
        btn.classList.toggle('is-active', isActive);
        const label = isActive ? 'Retirer de la liste de souhaits' : 'Ajouter à la liste de souhaits';
        btn.setAttribute('aria-label', label);
        btn.setAttribute('title', label);
      });
    }

    updateWishlistPage() {
      const pageContainer = document.querySelector('[data-wishlist-container]');
      if (!pageContainer) return;

      const grid = pageContainer.querySelector('[data-wishlist-grid]');
      const emptyState = pageContainer.querySelector('[data-wishlist-empty]');
      const actions = pageContainer.querySelector('[data-wishlist-actions]');

      if (this.items.length === 0) {
        if (grid) grid.style.display = 'none';
        if (emptyState) emptyState.style.display = 'block';
        if (actions) actions.style.display = 'none';
      } else {
        if (emptyState) emptyState.style.display = 'none';
        if (actions) actions.style.display = 'flex';
        if (grid) {
          grid.style.display = 'grid';
          grid.innerHTML = this.items.map((item) => this.renderCard(item)).join('');
        }
      }

      this.renderRecommendations();
    }

    renderCard(item) {
      const imgUrl = item.image || '';
      const priceText = item.price || '';
      const titleSafe = this.escapeHtml(item.title);
      const urlSafe = this.escapeHtml(item.url || '#');

      return `
        <div class="wishlist-card" data-product-id="${item.id}">
          <div class="wishlist-card__image-wrapper">
            <a href="${urlSafe}" class="wishlist-card__image-link">
              ${imgUrl ? `<img src="${imgUrl}" alt="${titleSafe}" class="wishlist-card__image" loading="lazy">` : `<div style="width:100%;height:100%;background:#FAF5F0;"></div>`}
            </a>
            <button type="button" class="wishlist-card__remove-btn" data-wishlist-remove="${item.id}" title="Retirer" aria-label="Retirer des favoris">
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          <div class="wishlist-card__content">
            <a href="${urlSafe}" class="wishlist-card__title">${titleSafe}</a>
            <div class="wishlist-card__price">${priceText}</div>
            <button type="button" class="wishlist-card__quick-add" data-wishlist-quick-add data-variant-id="${item.variantId || ''}">
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="9" cy="21" r="1"></circle>
                <circle cx="20" cy="21" r="1"></circle>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
              </svg>
              <span>Ajouter au panier</span>
            </button>
          </div>
        </div>
      `;
    }

    recordRecentlyViewed() {
      if (!window.location.pathname.includes('/products/')) return;
      const btn = document.querySelector('.product-media__wishlist-btn, .product-3d__wishlist-btn, [data-wishlist-btn]');
      let prod = null;
      if (btn) {
        prod = {
          id: btn.getAttribute('data-product-id'),
          handle: btn.getAttribute('data-product-handle'),
          title: btn.getAttribute('data-product-title'),
          price: btn.getAttribute('data-product-price'),
          image: btn.getAttribute('data-product-image'),
          url: btn.getAttribute('data-product-url') || window.location.pathname,
          variantId: btn.getAttribute('data-variant-id')
        };
      } else if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product) {
        const p = window.ShopifyAnalytics.meta.product;
        prod = {
          id: String(p.id),
          handle: p.handle,
          title: document.title.split('–')[0].split('-')[0].trim(),
          price: '',
          image: '',
          url: window.location.pathname,
          variantId: (p.variants && p.variants[0]) ? String(p.variants[0].id) : ''
        };
      }

      if (!prod || !prod.id || !prod.title) return;

      try {
        let list = JSON.parse(localStorage.getItem('similr_recently_viewed') || '[]');
        if (!Array.isArray(list)) list = [];
        list = list.filter((item) => String(item.id) !== String(prod.id));
        list.unshift(prod);
        if (list.length > 12) list = list.slice(0, 12);
        localStorage.setItem('similr_recently_viewed', JSON.stringify(list));
      } catch (e) {
        console.warn('[Similr] Erreur consultation produit', e);
      }
    }

    renderRecommendations() {
      const recContainer = document.querySelector('[data-wishlist-recommendations]');
      if (!recContainer) return;

      const recGrid = recContainer.querySelector('[data-recommendations-grid]');
      const recTitle = recContainer.querySelector('[data-rec-title]');
      const recSubtitle = recContainer.querySelector('[data-rec-subtitle]');
      if (!recGrid) return;

      let recentList = [];
      try {
        recentList = JSON.parse(localStorage.getItem('similr_recently_viewed') || '[]');
        if (!Array.isArray(recentList)) recentList = [];
      } catch (e) {
        recentList = [];
      }

      // Filtrer les articles déjà dans la liste de souhaits
      const candidates = recentList.filter((item) => item && item.id && !this.hasItem(item.id));
      const fallbackCards = Array.from(recGrid.querySelectorAll('[data-rec-fallback]'));

      if (candidates.length > 0) {
        if (recTitle) recTitle.textContent = 'Vous pourriez aussi aimer';
        if (recSubtitle) recSubtitle.textContent = 'Sélectionnées d\'après vos visites et vos articles consultés';

        const itemsToRender = candidates.slice(0, 4);
        let html = itemsToRender.map((item) => this.renderRecCard(item)).join('');

        if (itemsToRender.length < 4 && fallbackCards.length > 0) {
          const needed = 4 - itemsToRender.length;
          let added = 0;
          fallbackCards.forEach((fb) => {
            const fbId = fb.getAttribute('data-product-id');
            const alreadyShown = itemsToRender.some((it) => String(it.id) === String(fbId));
            if (!this.hasItem(fbId) && !alreadyShown && added < needed) {
              html += fb.outerHTML;
              added++;
            }
          });
        }

        recGrid.innerHTML = html;
        recContainer.style.display = 'block';
      } else if (fallbackCards.length > 0) {
        if (recTitle) recTitle.textContent = 'Vous pourriez aussi aimer';
        if (recSubtitle) recSubtitle.textContent = 'Nos créations les plus appréciées pour votre bébé';

        let visibleCount = 0;
        fallbackCards.forEach((fb) => {
          const fbId = fb.getAttribute('data-product-id');
          if (this.hasItem(fbId)) {
            fb.style.display = 'none';
          } else {
            fb.style.display = 'flex';
            visibleCount++;
          }
        });

        recContainer.style.display = visibleCount > 0 ? 'block' : 'none';
      } else {
        recContainer.style.display = 'none';
      }

      this.updateHeartButtons();
    }

    renderRecCard(item) {
      const imgUrl = item.image || '';
      const priceText = item.price || '';
      const titleSafe = this.escapeHtml(item.title);
      const urlSafe = this.escapeHtml(item.url || '#');

      return `
        <div class="wishlist-card wishlist-card--rec" data-product-id="${item.id}">
          <div class="wishlist-card__image-wrapper">
            <a href="${urlSafe}" class="wishlist-card__image-link">
              ${imgUrl ? `<img src="${imgUrl}" alt="${titleSafe}" class="wishlist-card__image" loading="lazy">` : `<div style="width:100%;height:100%;background:#FAF5F0;"></div>`}
            </a>
            <button
              type="button"
              class="card__wishlist-btn"
              data-wishlist-btn
              data-product-id="${item.id}"
              data-product-handle="${this.escapeHtml(item.handle || '')}"
              data-product-title="${titleSafe}"
              data-product-price="${this.escapeHtml(priceText)}"
              data-product-image="${this.escapeHtml(imgUrl)}"
              data-product-url="${urlSafe}"
              data-variant-id="${item.variantId || ''}"
              aria-label="Ajouter à la liste de souhaits"
              title="Ajouter à la liste de souhaits"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#C88D79" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
              </svg>
            </button>
          </div>
          <div class="wishlist-card__content">
            <a href="${urlSafe}" class="wishlist-card__title">${titleSafe}</a>
            <div class="wishlist-card__price">${priceText}</div>
            <button type="button" class="wishlist-card__quick-add" data-wishlist-quick-add data-variant-id="${item.variantId || ''}">
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="9" cy="21" r="1"></circle>
                <circle cx="20" cy="21" r="1"></circle>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
              </svg>
              <span>Ajouter au panier</span>
            </button>
          </div>
        </div>
      `;
    }

    escapeHtml(text) {
      if (!text) return '';
      return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    showToast(message, type = 'added') {
      let toast = document.querySelector('.wishlist-toast');
      if (!toast) {
        toast = document.createElement('div');
        toast.className = 'wishlist-toast';
        document.body.appendChild(toast);
      }

      let iconSvg = '';
      if (type === 'heart' || type === 'added') {
        iconSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="#C88D79" stroke="#C88D79" stroke-width="1.3"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>`;
      } else if (type === 'removed') {
        iconSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#A89690" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>`;
      } else if (type === 'cart') {
        iconSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#C88D79" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>`;
      } else if (type === 'check') {
        iconSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#7DAA83" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>`;
      } else {
        iconSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#8C756B" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>`;
      }

      toast.innerHTML = `<span class="wishlist-toast__icon">${iconSvg}</span><span class="wishlist-toast__text">${this.escapeHtml(message)}</span>`;
      toast.classList.add('is-visible');

      if (this.toastTimeout) clearTimeout(this.toastTimeout);
      this.toastTimeout = setTimeout(() => {
        toast.classList.remove('is-visible');
      }, 2400);
    }

    hasAcceptedCookies() {
      // 1. Via l'API globale SimilrCookies si prête
      if (window.SimilrCookies && typeof window.SimilrCookies.hasAccepted === 'function') {
        if (window.SimilrCookies.hasAccepted()) return true;
      }

      // 2. Via le cookie persistant HTTP
      try {
        const match = document.cookie.match(/(?:^|;\s*)similr_cookie_consent=([^;]*)/);
        if (match && match[1]) {
          const decoded = decodeURIComponent(match[1]);
          if (decoded.includes('"accepted":true') || decoded.includes('accepted')) {
            return true;
          }
          const parsed = JSON.parse(decoded);
          if (parsed && parsed.accepted === true) return true;
        }
      } catch (e) {}

      // 3. Via LocalStorage
      try {
        const ls = localStorage.getItem('similr_cookie_consent_v1');
        if (ls) {
          const parsed = JSON.parse(ls);
          if (parsed && parsed.accepted === true) return true;
        }
      } catch (e) {}

      // 4. Via SessionStorage
      try {
        const ss = sessionStorage.getItem('similr_cookie_consent_v1');
        if (ss) {
          const parsed = JSON.parse(ss);
          if (parsed && parsed.accepted === true) return true;
        }
      } catch (e) {}

      // 5. Via l'API native Shopify Customer Privacy
      if (window.Shopify && window.Shopify.customerPrivacy) {
        if (typeof window.Shopify.customerPrivacy.getTrackingConsent === 'function') {
          if (window.Shopify.customerPrivacy.getTrackingConsent() === 'yes') {
            return true;
          }
        }
      }

      return false;
    }

    checkCookieBanner() {
      const banner = document.querySelector('[data-wishlist-cookie-banner]');
      if (!banner) return;

      const hasAccepted = this.hasAcceptedCookies();

      if (hasAccepted) {
        banner.style.display = 'none';
      } else {
        banner.style.display = 'flex';
      }
    }

    checkPageFallback() {
      const path = window.location.pathname.toLowerCase();
      const isWishlistUrl = path.includes('/pages/wishlist') || path.includes('/wishlist');
      if (!isWishlistUrl) return;

      document.title = 'Ma Liste de Souhaits - ' + (window.Shopify && window.Shopify.shop ? window.Shopify.shop : 'Simil-R');

      const existingContainer = document.querySelector('[data-wishlist-container]');
      if (!existingContainer) {
        const targetContainer = document.querySelector('.template-404, #MainContent, main');
        if (targetContainer) {
          targetContainer.innerHTML = `
            <div class="wishlist-page page-width" data-wishlist-container style="padding: 2.5rem 1.5rem 6rem;">
              <nav class="wishlist-page__nav" aria-label="Navigation">
                <button
                  type="button"
                  class="wishlist-page__back-link wishlist-page__back-btn"
                  onclick="if (window.history.length > 1 && document.referrer && document.referrer.indexOf(window.location.host) !== -1) { window.history.back(); } else { window.location.href = '/'; }"
                  aria-label="Retour en arrière"
                >
                  <svg class="icon icon-back-arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M19 12H5M12 19l-7-7 7-7"/>
                  </svg>
                  <span>Retour</span>
                </button>
              </nav>

              <header class="wishlist-page__header">
                <div class="wishlist-page__badge">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="#C88D79" stroke="#C88D79" stroke-width="2">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                  </svg>
                  <span>Mes Favoris</span>
                </div>
                <h1 class="wishlist-page__title">Ma Liste de Souhaits</h1>
                <p class="wishlist-page__subtitle">
                  Retrouvez ici tous vos articles coups de cœur et préparez l'arrivée de votre tout-petit en toute sérénité.
                </p>
              </header>

              <div class="wishlist-cookie-banner" data-wishlist-cookie-banner style="display: none;">
                <div class="wishlist-cookie-banner__content">
                  <div class="wishlist-cookie-banner__icon">🍪</div>
                  <div class="wishlist-cookie-banner__text">
                    <h4>Conservez vos coups de cœur pour votre prochaine visite</h4>
                    <p>Vos articles sont actuellement gardés pour cette session. Pour les retrouver quand vous reviendrez sur le site, veuillez autoriser les cookies.</p>
                  </div>
                </div>
                <div class="wishlist-cookie-banner__actions">
                  <button type="button" class="wishlist-cookie-banner__btn" data-action="wishlist-accept-cookies">
                    Accepter les cookies
                  </button>
                </div>
              </div>

              <div class="wishlist-grid" data-wishlist-grid style="display: none;"></div>

              <div class="wishlist-page__actions" data-wishlist-actions style="display: none;">
                <button
                  type="button"
                  class="wishlist-page__continue-btn wishlist-page__back-btn"
                  onclick="if (window.history.length > 1 && document.referrer && document.referrer.indexOf(window.location.host) !== -1) { window.history.back(); } else { window.location.href = '/'; }"
                  aria-label="Retour en arrière"
                >
                  <svg class="icon icon-back-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M19 12H5M12 19l-7-7 7-7"/>
                  </svg>
                  <span>Retour aux achats</span>
                </button>
              </div>

              <div class="wishlist-empty" data-wishlist-empty style="display: none;">
                <div class="wishlist-empty__icon-svg">
                  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#C88D79" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                  </svg>
                </div>
                <h2 class="wishlist-empty__title">Votre liste de souhaits est vide</h2>
                <p class="wishlist-empty__desc">
                  Explorez notre sélection douce et poétique d'équipements pour bébé, et enregistrez vos créations favorites en un clic.
                </p>
                <button
                  type="button"
                  class="wishlist-empty__cta wishlist-empty__back-btn"
                  onclick="if (window.history.length > 1 && document.referrer && document.referrer.indexOf(window.location.host) !== -1) { window.history.back(); } else { window.location.href = '/'; }"
                  aria-label="Retour en arrière"
                >
                  <svg class="icon icon-back-arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M19 12H5M12 19l-7-7 7-7"/>
                  </svg>
                  <span>Retour en arrière</span>
                </button>
              </div>

              <section class="wishlist-recommendations" data-wishlist-recommendations style="display: none;">
                <div class="wishlist-recommendations__header">
                  <div class="wishlist-page__badge">
                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#C88D79" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
                    </svg>
                    <span>Inspirations</span>
                  </div>
                  <h2 class="wishlist-recommendations__title" data-rec-title>Vous pourriez aimer</h2>
                  <p class="wishlist-recommendations__subtitle" data-rec-subtitle>
                    Sélectionnées pour vous d'après vos articles consultés
                  </p>
                </div>
                <div class="wishlist-recommendations__grid" data-recommendations-grid></div>
              </section>
            </div>
          `;
          this.updateWishlistPage();
          this.checkCookieBanner();
        }
      }
    }

    async handleQuickAdd(btn) {
      const variantId = btn.getAttribute('data-variant-id');
      if (!variantId) {
        const card = btn.closest('.wishlist-card');
        const link = card ? card.querySelector('.wishlist-card__title') : null;
        if (link && link.href) {
          window.location.href = link.href;
        }
        return;
      }

      btn.classList.add('is-loading');
      btn.disabled = true;
      const originalHtml = btn.innerHTML;
      btn.innerHTML = `<span>Ajout en cours...</span>`;

      try {
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const bodyObj = {
          id: variantId,
          quantity: 1,
          sections: cart && typeof cart.getSectionsToRender === 'function' ? cart.getSectionsToRender().map((s) => s.id) : []
        };

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript'
          },
          body: JSON.stringify(bodyObj)
        });

        const data = await res.json();

        if (res.ok) {
          btn.classList.remove('is-loading');
          btn.classList.add('is-success');
          btn.innerHTML = `<span>✓ Ajouté !</span>`;

          if (cart) {
            cart.renderContents(data);
          } else {
            // Update cart bubble manually if drawer not mounted
            const cartBubble = document.querySelector('#cart-icon-bubble');
            if (cartBubble) {
              const resCart = await fetch('/cart.js');
              const cartJson = await resCart.json();
              const countEl = cartBubble.querySelector('.cart-count-bubble span[aria-hidden="true"]');
              if (countEl) countEl.textContent = cartJson.item_count;
            }
          }

          if (window.publish && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'wishlist', cartData: data });
          }

          this.showToast('Ajouté au panier', 'cart');

          setTimeout(() => {
            btn.classList.remove('is-success');
            btn.innerHTML = originalHtml;
            btn.disabled = false;
          }, 2000);
        } else {
          throw new Error(data.description || 'Erreur lors de l\'ajout au panier');
        }
      } catch (err) {
        console.error('[Similr Wishlist] Erreur quick add', err);
        btn.classList.remove('is-loading');
        btn.innerHTML = originalHtml;
        btn.disabled = false;
        this.showToast(err.message || 'Article indisponible', 'info');
      }
    }

    init() {
      this.recordRecentlyViewed();

      const onReady = () => {
        this.recordRecentlyViewed();
        this.checkPageFallback();
        this.updateUI();
        this.checkCookieBanner();
      };

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', onReady);
      } else {
        onReady();
      }

      // Synchronisation au consentement cookies (Simil-R & Shopify)
      window.addEventListener('similr:cookieConsentUpdated', () => {
        this.checkCookieBanner();
      });
      document.addEventListener('shopify:customerPrivacy:consentChanged', () => {
        this.checkCookieBanner();
      });

      // Revérification après chargement de scripts différés
      setTimeout(() => this.checkCookieBanner(), 300);
      setTimeout(() => this.checkCookieBanner(), 1000);

      // Intercepter pointerdown/mousedown pour stopper toute propagation vers les liens parents
      document.addEventListener(
        'pointerdown',
        (e) => {
          const heartBtn = e.target.closest('[data-wishlist-btn]');
          if (heartBtn) {
            e.stopPropagation();
          }
        },
        true
      );

      // Gestionnaires d'événements au clic (délégation)
      document.addEventListener(
        'click',
        (e) => {
          // Clic sur l'icône cœur
          const heartBtn = e.target.closest('[data-wishlist-btn]');
          if (heartBtn) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            const item = {
              id: heartBtn.getAttribute('data-product-id'),
              handle: heartBtn.getAttribute('data-product-handle'),
              title: heartBtn.getAttribute('data-product-title'),
              price: heartBtn.getAttribute('data-product-price'),
              image: heartBtn.getAttribute('data-product-image'),
              url: heartBtn.getAttribute('data-product-url'),
              variantId: heartBtn.getAttribute('data-variant-id')
            };
            this.toggleItem(item);
            return;
          }

          // Clic sur le bouton supprimer de la carte wishlist
          const removeBtn = e.target.closest('[data-wishlist-remove]');
          if (removeBtn) {
            e.preventDefault();
            e.stopPropagation();
            const productId = removeBtn.getAttribute('data-wishlist-remove');
            const card = removeBtn.closest('.wishlist-card');
            if (card) {
              card.style.transition = 'opacity 0.25s ease, transform 0.25s ease';
              card.style.opacity = '0';
              card.style.transform = 'scale(0.9)';
              setTimeout(() => {
                this.removeItem(productId);
              }, 250);
            } else {
              this.removeItem(productId);
            }
            return;
          }

          // Clic sur l'ajout rapide au panier
        const quickAddBtn = e.target.closest('[data-wishlist-quick-add]');
        if (quickAddBtn) {
          e.preventDefault();
          this.handleQuickAdd(quickAddBtn);
          return;
        }

        // Clic sur le bouton d'acceptation des cookies de la bannière wishlist
        const acceptCookieBtn = e.target.closest('[data-action="wishlist-accept-cookies"]');
        if (acceptCookieBtn) {
          e.preventDefault();
          if (window.SimilrCookies && typeof window.SimilrCookies.acceptAll === 'function') {
            window.SimilrCookies.acceptAll();
          } else {
            const consentRecord = {
              accepted: true,
              categories: { essential: true, preferences: true, analytics: true, marketing: true },
              timestamp: new Date().toISOString(),
              version: '1.0'
            };
            const cookieStr = `similr_cookie_consent=accepted; path=/; max-age=31536000; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`;
            document.cookie = cookieStr;
            try {
              localStorage.setItem('similr_cookie_consent', JSON.stringify(consentRecord));
            } catch (err) {}
          }

          const banner = document.querySelector('[data-wishlist-cookie-banner]');
          if (banner) {
            banner.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
            banner.style.opacity = '0';
            banner.style.transform = 'translateY(-10px)';
            setTimeout(() => {
              banner.style.display = 'none';
            }, 300);
          }
          this.showToast('Préférences enregistrées', 'check');
          return;
        }
      });
    }
  }

  // Instancier et exposer globalement
  window.SimilrWishlist = new SimilrWishlist();
})();
